import React, { useState, useEffect, useRef, useContext } from 'react'
import { FilterMatchMode, FilterOperator } from 'primereact/api'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { ColumnGroup } from 'primereact/columngroup'
import { Toolbar } from 'primereact/toolbar'
import { Button } from 'primereact/button'
import { Row } from 'primereact/row'
import { Dialog } from 'primereact/dialog'
import { Toast } from 'primereact/toast'

import MainContentLayout from '../../Layout/MainContentLayout'
import {
  DateFilterTemplate,
  DropDownTemplate,
  InputFieldTemplate,
  MultiSelectTemplate,
  SingleDateFilterTemplate,
} from '../../shared/FilterTemplates'
import server from '../../../server'
import {
  formatCurrency,
  formatDate,
  formatPercentage,
  ToastPopUp,
} from '../../shared/utils'
import { AuthenticationContext } from '../../../Auth/authentication.context'
import useTableRowsPerPage from '../../../hooks/useTableRowsPerPage'
import { getInitialPaginationState } from '../../../shared/tablePagination'
import ReportScopeSummary from './ReportScopeSummary'
import {
  ORDER_STATUS_OPTIONS,
  MANUFACTURER_REPORT_SCOPE,
  buildReportParams,
} from './reportFilters'

export default function ManufacturersAdmin() {
  const dt = useRef(null)
  const toast = useRef(null)
  const { user } = useContext(AuthenticationContext)
  const { defaultRows, rowsPerPageOptions, applyPageEvent } = useTableRowsPerPage()

  const [manufacturers, setManufacturers] = useState()
  const [tableLoading, setTableLoading] = useState(true)
  const [clearBtnDisabled, setClearBtnDisabled] = useState(true)
  const [state, setState] = useState(() => getInitialPaginationState(user))

  useEffect(() => {
    setState((prev) => ({ ...prev, rows: defaultRows }))
  }, [defaultRows])
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  // Empty = every reportable status. Cancelled is never an option.
  const [statuses, setStatuses] = useState([])
  // What the current filters counted and what they left out, as reported by the
  // API alongside the rows.
  const [scope, setScope] = useState(null)

  // Reconciliation report dialog state.
  const isAdmin = user?.role === 'admin'
  const [reconVisible, setReconVisible] = useState(false)
  const [reconGenerating, setReconGenerating] = useState(false)
  const [manufacturerOptions, setManufacturerOptions] = useState([])
  const [reconManufacturerId, setReconManufacturerId] = useState(null)
  const [reconStartDate, setReconStartDate] = useState(null)
  const [reconEndDate, setReconEndDate] = useState(null)
  const [reconStatuses, setReconStatuses] = useState([])
  const [reconPreview, setReconPreview] = useState(null)
  const [reconPreviewLoading, setReconPreviewLoading] = useState(false)

  // Load the manufacturer list for the report dropdown (admins only).
  useEffect(() => {
    if (!isAdmin) return
    server
      .get('/manufacturers/?ordable=true')
      .then((response) => {
        const list = response?.data?.Manufacturers || []
        setManufacturerOptions(
          list.map((m) => ({ name: m.name, value: m.id }))
        )
      })
      .catch(() => {})
  }, [isAdmin])

  const openReconDialog = () => {
    // Seed the dialog from the filters already applied to the table, so the
    // report someone generates matches the numbers they are looking at.
    setReconManufacturerId(null)
    setReconStartDate(startDate)
    setReconEndDate(endDate)
    setReconStatuses(statuses)
    setReconPreview(null)
    setReconVisible(true)
  }

  const reconParams = () =>
    buildReportParams({
      startDate: reconStartDate,
      endDate: reconEndDate,
      statuses: reconStatuses,
      formatDate,
      extra: { manufacturerId: reconManufacturerId },
    })

  // Ask the API what the current dialog filters would produce, so the report is
  // reviewed before it is downloaded and attached to an invoice.
  useEffect(() => {
    if (!reconVisible || !reconManufacturerId) {
      setReconPreview(null)
      return
    }

    let cancelled = false
    setReconPreviewLoading(true)
    server
      .get(`/manufacturers/reconciliation-report/preview${reconParams()}`)
      .then((response) => {
        if (cancelled) return
        setReconPreview(response.data)
        setReconPreviewLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setReconPreview(null)
        setReconPreviewLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [
    reconVisible,
    reconManufacturerId,
    reconStartDate,
    reconEndDate,
    reconStatuses,
  ])

  const generateReconciliationReport = () => {
    if (!reconManufacturerId) {
      ToastPopUp(
        toast,
        'warn',
        'Manufacturer required',
        'Please select a manufacturer.'
      )
      return
    }

    const params = reconParams()

    setReconGenerating(true)
    server
      .get(`/manufacturers/reconciliation-report/export${params}`, {
        responseType: 'blob',
      })
      .then((response) => {
        const blobData = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
        const manufacturerName =
          manufacturerOptions.find((m) => m.value === reconManufacturerId)
            ?.name || 'Manufacturer'
        const safeName = manufacturerName.replace(/[^\w-]+/g, '_')
        const url = window.URL.createObjectURL(blobData)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `Reconciliation_Report_${safeName}.xlsx`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        setReconGenerating(false)
        setReconVisible(false)
      })
      .catch(() => {
        setReconGenerating(false)
        ToastPopUp(
          toast,
          'error',
          'Report failed',
          'Could not generate the reconciliation report.'
        )
      })
  }

  const getData = () => {
    setTableLoading(true)

    const params = buildReportParams({
      startDate,
      endDate,
      statuses,
      formatDate,
    })

    server
      .get(`/manufacturers/admin/${params}`)
      .then((response) => {
        setManufacturers(response.data.Manufacturers)
        setScope({
          criteria: response.data.criteria,
          excludedByStatus: response.data.excludedByStatus || [],
        })
        setTableLoading(false)
      })
      .catch((error) => {
        setTableLoading(false)
        // setError(error.response.data.message)
      })
  }

  useEffect(() => {
    checkClearBtn()
    return getData()
  }, [state, startDate, endDate, statuses])

  useEffect(() => initState(), [])

  const checkClearBtn = () => {
    if (state.sortField || state.sortOrder || statuses.length > 0) {
      setClearBtnDisabled(false)
      return
    }
    for (const key in state?.filters) {
      if (key === 'global') {
        continue
      } else if (
        state?.filters[key].constraints[0].value ||
        state?.filters[key]?.constraints[1]?.value
      ) {
        setClearBtnDisabled(false)
        return
      }
    }
    setClearBtnDisabled(true)
  }

  const clearFilter = () => {
    initState()
  }

  const initState = () => {
    setState((prevState) => ({
      ...prevState,
      sortField: '',
      sortOrder: '',
      filters: {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        'manufacturer.name': {
          operator: FilterOperator.AND,
          constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
        },
        totalExtendedPrice: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: 'gte' },
            { value: null, matchMode: 'lte' },
          ],
        },
        totalRefundedAmount: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: 'gte' },
            { value: null, matchMode: 'lte' },
          ],
        },
        averagePa: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: 'gte' },
            { value: null, matchMode: 'lte' },
          ],
        },
        singleSourceTotalExtendedPrice: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: 'gte' },
            { value: null, matchMode: 'lte' },
          ],
        },
        singleSourceTotalRefunded: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: 'gte' },
            { value: null, matchMode: 'lte' },
          ],
        },
        singleSourceAveragePa: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: 'gte' },
            { value: null, matchMode: 'lte' },
          ],
        },
        dualSourceTotalExtendedPrice: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: 'gte' },
            { value: null, matchMode: 'lte' },
          ],
        },
        dualSourceTotalRefunded: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: 'gte' },
            { value: null, matchMode: 'lte' },
          ],
        },
        dualSourceAveragePa: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: 'gte' },
            { value: null, matchMode: 'lte' },
          ],
        },
        multiSourceTotalExtendedPrice: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: 'gte' },
            { value: null, matchMode: 'lte' },
          ],
        },
        multiSourceTotalRefunded: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: 'gte' },
            { value: null, matchMode: 'lte' },
          ],
        },
        multiSourceAveragePa: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: 'gte' },
            { value: null, matchMode: 'lte' },
          ],
        },
      },
    }))
    setStartDate(null)
    setEndDate(null)
    setStatuses([])
  }

  // TOOLBAR
  const topLeftToolbarTemplate = () => (
    <div className='d-flex gap-2'>
      <Button
        type='button'
        icon='pi pi-filter-slash'
        label='Clear'
        outlined
        onClick={clearFilter}
        disabled={clearBtnDisabled}
      />
      {isAdmin && (
        <Button
          type='button'
          icon='pi pi-file-excel'
          label='Reconciliation Report'
          severity='success'
          onClick={openReconDialog}
        />
      )}
    </div>
  )

  const topRightToolbarTemplate = () => (
    <>
      <div className='d-flex gap-1 align-items-center flex-wrap justify-content-end'>
        <MultiSelectTemplate
          state={statuses}
          stateHandler={setStatuses}
          options={ORDER_STATUS_OPTIONS}
          optionValue='value'
          placeholder='All Order Statuses'
          maxSelectedLabels={2}
          showClear
        />
        <SingleDateFilterTemplate
          state={startDate}
          stateHandler={(e) => setStartDate(e.value)}
          placeholder='From'
          showIcon={false}
        />
        <SingleDateFilterTemplate
          state={endDate}
          stateHandler={(e) => setEndDate(e.value)}
          placeholder='To'
          disabled={!startDate}
          minDate={startDate}
        />
      </div>
    </>
  )

  // BODY TEMPLATES
  const orderAmountBodyTemplate = (order) =>
    formatCurrency(order.totalExtendedPrice)

  const paAmountBodyTemplate = (order) =>
    formatCurrency(order.totalRefundedAmount)

  const paPercentageBodyTemplate = (order) => formatPercentage(order.averagePa)

  // FILTER TEMPLATES
  const manufacturerFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      matchMode={options.filterModel.matchMode}
      placehloder='Manufacturer'
      icon='mdi mdi-cog'
    />
  )
  const orderAmountFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      matchMode={options.filterModel.matchMode}
      icon='mdi mdi-currency-usd'
      type='number'
    />
  )
  const paAmountFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      matchMode={options.filterModel.matchMode}
      icon='mdi mdi-currency-usd'
      type='number'
    />
  )
  const paPercentageFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      matchMode={options.filterModel.matchMode}
      icon='mdi mdi-percent-outline'
      type='number'
    />
  )

  const headerGroup = (
    <ColumnGroup>
      <Row>
        <Column
          field='manufacturer.name'
          header='Manufacturer Name'
          rowSpan={2}
          sortable
          filter
          showFilterMenuOptions={false}
          filterElement={manufacturerFilterElement}
        />
        <Column
          field='totalExtendedPrice'
          header='Total Order Amount'
          rowSpan={2}
          sortable
          filter
          showFilterMenuOptions={false}
          filterElement={orderAmountFilterElement}
        />
        <Column
          field='totalRefundedAmount'
          header='Total PA Amount'
          rowSpan={2}
          sortable
          filter
          showFilterMenuOptions={false}
          filterElement={paAmountFilterElement}
        />
        <Column
          field='averagePa'
          header='Average PA Percentage'
          rowSpan={2}
          sortable
          filter
          showFilterMenuOptions={false}
          filterElement={paPercentageFilterElement}
        />
        <Column colSpan={3} header='Single Source Molecule' />
        <Column colSpan={3} header='Dual Source Molecule' />
        <Column colSpan={3} header='Multi Source Molecule' />
      </Row>
      <Row>
        <Column
          header='Order Amount'
          field='singleSourceTotalExtendedPrice'
          sortable
        />
        <Column header='PA Amount' field='singleSourceTotalRefunded' sortable />
        <Column header='PA Percentage' field='singleSourceAveragePa' sortable />
        <Column
          header='Order Amount'
          field='dualSourceTotalExtendedPrice'
          sortable
        />
        <Column header='PA Amount' field='dualSourceTotalRefunded' sortable />
        <Column header='PA Percentage' field='dualSourceAveragePa' sortable />
        <Column
          header='Order Amount'
          field='multiSourceTotalExtendedPrice'
          sortable
        />
        <Column header='PA Amount' field='multiSourceTotalRefunded' sortable />
        <Column header='PA Percentage' field='multiSourceAveragePa' sortable />
      </Row>
    </ColumnGroup>
  )
  const reconDialogFooter = (
    <div className='d-flex justify-content-end gap-2'>
      <Button
        label='Cancel'
        icon='pi pi-times'
        outlined
        onClick={() => setReconVisible(false)}
        disabled={reconGenerating}
      />
      <Button
        label='Generate'
        icon='pi pi-download'
        severity='success'
        onClick={generateReconciliationReport}
        loading={reconGenerating}
        disabled={!reconManufacturerId}
      />
    </div>
  )

  return (
    <MainContentLayout title='Manufacturers Report' dt={dt}>
      <Toast ref={toast} />
      <Dialog
        header='Generate Reconciliation Report'
        visible={reconVisible}
        onHide={() => {
          if (!reconGenerating) setReconVisible(false)
        }}
        style={{ width: '32rem' }}
        breakpoints={{ '960px': '90vw' }}
        footer={reconDialogFooter}
        dismissableMask={!reconGenerating}
        // The preview grows with the number of excluded statuses, so keep the
        // dialog on screen and scroll its body instead.
        contentStyle={{ maxHeight: '65vh', overflowY: 'auto' }}
      >
        <div className='d-flex flex-column gap-3 pt-2'>
          <div className='d-flex flex-column gap-1'>
            <label className='fw-semibold'>
              Manufacturer <span className='text-danger'>*</span>
            </label>
            <DropDownTemplate
              state={reconManufacturerId}
              stateHandler={(e) => setReconManufacturerId(e.value)}
              optionValue='value'
              placeholder='Select a manufacturer'
              options={manufacturerOptions}
            />
          </div>

          <small className='text-muted'>{MANUFACTURER_REPORT_SCOPE}</small>
          <small className='text-muted'>
            Besides the orders sheet, the workbook breaks the same orders down
            by molecule (order date, pharmacy, DIN, UPC, item code and amount)
            and summarises sales per product, for manufacturers that reconcile
            without the Origins order #.
          </small>

          <div className='d-flex flex-column gap-1'>
            <label className='fw-semibold'>Order Status</label>
            <MultiSelectTemplate
              state={reconStatuses}
              stateHandler={setReconStatuses}
              options={ORDER_STATUS_OPTIONS}
              optionValue='value'
              placeholder='All Order Statuses'
              showClear
            />
            <small className='text-muted'>
              Cancelled orders are never included — they are not billable.
            </small>
          </div>

          <div className='d-flex flex-column gap-1'>
            <label className='fw-semibold'>Date Range</label>
            <div className='d-flex gap-2'>
              <SingleDateFilterTemplate
                state={reconStartDate}
                stateHandler={(e) => setReconStartDate(e.value)}
                placeholder='From'
                showIcon={false}
              />
              <SingleDateFilterTemplate
                state={reconEndDate}
                stateHandler={(e) => setReconEndDate(e.value)}
                placeholder='To'
                disabled={!reconStartDate}
                minDate={reconStartDate}
              />
            </div>
            <small className='text-muted'>
              Optional. Leave blank to include all orders.
            </small>
          </div>

          {/* What this report will contain, before it is downloaded and
              attached to an invoice. */}
          {reconManufacturerId && (
            <div className='border-top pt-3'>
              {reconPreviewLoading && (
                <span className='small text-muted'>
                  <i className='pi pi-spin pi-spinner me-2' />
                  Checking what these filters include…
                </span>
              )}
              {!reconPreviewLoading && reconPreview && (
                <>
                  <div className='d-flex justify-content-between align-items-baseline mb-2'>
                    <span className='fw-semibold'>
                      {reconPreview.includedOrders} order
                      {reconPreview.includedOrders === 1 ? '' : 's'} will be
                      included
                    </span>
                    <span className='fw-semibold'>
                      {formatCurrency(reconPreview.includedTotal)}
                    </span>
                  </div>
                  {/* The workbook also breaks these orders down by molecule
                      / DIN / UPC / item code, for manufacturers that cannot
                      match an Origins order # in their own system. */}
                  {typeof reconPreview.includedLineItems === 'number' && (
                    <div className='d-flex justify-content-between align-items-baseline mb-2 small text-muted'>
                      <span>
                        {reconPreview.includedMolecules} molecule
                        {reconPreview.includedMolecules === 1 ? '' : 's'} ·{' '}
                        {reconPreview.includedProducts} product
                        {reconPreview.includedProducts === 1 ? '' : 's'} ·{' '}
                        {reconPreview.includedLineItems} line item
                        {reconPreview.includedLineItems === 1 ? '' : 's'}
                      </span>
                      <span>
                        {formatCurrency(reconPreview.includedLineTotal)} in{' '}
                        {reconPreview.criteria?.manufacturer || 'this manufacturer'}
                        &apos;s products
                      </span>
                    </div>
                  )}
                  <ReportScopeSummary
                    criteria={reconPreview.criteria}
                    excludedByStatus={reconPreview.excludedByStatus}
                    excludedValueLabel='in order totals'
                    className='mb-0'
                  />
                </>
              )}
              {!reconPreviewLoading && !reconPreview && (
                <span className='small text-muted'>
                  Could not preview these filters. The report can still be
                  generated.
                </span>
              )}
            </div>
          )}
        </div>
      </Dialog>
      <Toolbar
        className='mb-2'
        left={topLeftToolbarTemplate}
        right={topRightToolbarTemplate}
      />
      {scope?.criteria && (
        <ReportScopeSummary
          criteria={scope.criteria}
          excludedByStatus={scope.excludedByStatus}
          excludedValueLabel='in line items'
        />
      )}
      <DataTable
        ref={dt}
        value={manufacturers}
        paginatorTemplate='FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
        rowsPerPageOptions={rowsPerPageOptions}
        paginator
        showGridlines
        loading={tableLoading}
        dataKey='manufacturer.name'
        removableSort
        globalFilterFields={['name']}
        headerColumnGroup={headerGroup}
        emptyMessage='No Manufactures Found.'
        sortField={state.sortField}
        sortOrder={state.sortOrder}
        filters={state.filters}
        rows={state.rows}
        first={state.first}
        onPage={(e) => setState((prev) => applyPageEvent(prev, e))}
        onSort={(e) => {
          e.first = 0
          setState((prev) => applyPageEvent(prev, e))
        }}
        onFilter={(e) => setState((prev) => applyPageEvent(prev, e))}
      >
        <Column field='manufacturer.name' />
        <Column field='totalExtendedPrice' body={orderAmountBodyTemplate} />
        <Column field='totalRefundedAmount' body={paAmountBodyTemplate} />
        <Column field='averagePa' body={paPercentageBodyTemplate} />

        <Column
          field='singleSourceTotalExtendedPrice'
          body={(order) =>
            formatCurrency(order?.singleSourceTotalExtendedPrice)
          }
        />
        <Column
          field='singleSourceTotalRefunded'
          body={(order) => formatCurrency(order?.singleSourceTotalRefunded)}
        />
        <Column
          field='singleSourceAveragePa'
          body={(order) => formatPercentage(order?.singleSourceAveragePa)}
        />
        <Column
          field='dualSourceTotalExtendedPrice'
          body={(order) => formatCurrency(order?.dualSourceTotalExtendedPrice)}
        />
        <Column
          field='dualSourceTotalRefunded'
          body={(order) => formatCurrency(order?.dualSourceTotalRefunded)}
        />
        <Column
          field='dualSourceAveragePa'
          body={(order) => formatPercentage(order?.dualSourceAveragePa)}
        />
        <Column
          field='multiSourceTotalExtendedPrice'
          body={(order) => formatCurrency(order?.multiSourceTotalExtendedPrice)}
        />
        <Column
          field='multiSourceTotalRefunded'
          body={(order) => formatCurrency(order?.multiSourceTotalRefunded)}
        />
        <Column
          field='multiSourceAveragePa'
          body={(order) => formatPercentage(order?.multiSourceAveragePa)}
        />
        
      </DataTable>
    </MainContentLayout>
  )
}
